// The console's frame (spec/13 §2, spec/14 §1, `apps/console/DESIGN.md`), modelled on the calm SaaS
// shells (Linear, Stripe): a slim grouped sidebar on the darkest ground with the mark at its head, and
// a quiet top bar — where you are (the venue switcher, or the HQ label), a search trigger that opens
// the command palette with its `Ctrl K` hint, the shift, notifications and the staff menu.
//
// The sidebar groups its sections by job (Operations / People / Setup …), and lists only the built sections
// the member's role may open (`sections.ts`, HQ's `hq/sections.ts`) — never a "not permitted" screen or a
// placeholder. On a narrow screen the sidebar folds away and the same navigation opens from the top bar in a
// sheet from the left.

import { useTranslation } from '@clavian/i18n/react';
import { Avatar, Button, Kbd, Menu, RouteTransition, Sheet, type MenuEntry } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { useSession } from '../api/queries';
import { requestRevealSetting, settingKeyFromHash } from '../settings/reveal';
import type { SettingDoc, SettingsTarget } from '../settings/search';
import { useSettingsSearch } from '../settings/useSettingsSearch';
import { CommandPalette, type PaletteSection } from './CommandPalette';
import { Mark } from './Mark';
import { Icon, NavIcon, SearchIcon } from './nav-icons';
import { NotificationsBell } from './NotificationsBell';
import type { ConsoleScope } from './scope';
import { setConsoleTheme, useConsoleTheme } from './theme';

export interface NavItem {
  /** Stable across renders: the palette and the icon are keyed by it. */
  readonly id: string;
  readonly label: string;
  readonly icon: ReactNode;
  readonly to: string;
  readonly params?: Readonly<Record<string, string>>;
  /**
   * Whether the page shown is this item's, when that is more than "the URL starts with its link" —
   * HQ's Platform settings is three pages under two paths. Absent: the router's own answer.
   */
  readonly current?: boolean;
  /** Current only on its own path, not below it: the venue's Floor is its root. */
  readonly exact?: boolean;
  /**
   * A short count beside the label — Setup's "5 left" (spec/13 §13.1) — with the words a screen reader hears for the
   * whole link ("Setup, 5 steps left"), since the count alone means nothing read aloud.
   */
  readonly badge?: { readonly text: string; readonly label: string };
}

export interface NavGroup {
  readonly id: string;
  readonly label: string;
  readonly items: readonly NavItem[];
}

/** A palette entry beyond navigation — an action the shell can run from anywhere. */
export interface PaletteAction {
  readonly id: string;
  readonly label: string;
  readonly icon: ReactNode;
  readonly hint?: string;
  /** Run once the palette has closed — an action that opens another layer (the venue switcher). */
  readonly afterClose?: boolean;
  readonly run: () => void;
}

export interface AppShellProps {
  /** What the top bar says about where you are: the venue switcher, or the HQ label. */
  readonly area: ReactNode;
  readonly groups: readonly NavGroup[];
  /**
   * The organisation whose inbox the notifications bell reads (F-NTF-03). Present in the venue and
   * organisation areas; absent in HQ, where the bell shows its empty state.
   */
  readonly organizationId?: string;
  /** The sidebar's name for a screen reader. */
  readonly navLabel?: string;
  /** Extra command-palette entries (an "Actions" group), the shell's own on top. */
  readonly actions?: readonly PaletteAction[];
  /**
   * The settings screens the palette searches (spec/13 §13, spec/14 §14): the ones this member can
   * open from here. Each is read only while the palette is open.
   */
  readonly settingsSearch?: readonly SettingsTarget[];
  /** The top bar's live status: the venue's shift. */
  readonly status?: ReactNode;
  /** The foot of the sidebar: the organisation's settings. */
  readonly sidebarFoot?: ReactNode;
  /** Extra entries in the staff menu, above Sign out. */
  readonly staffMenuItems?: readonly MenuEntry[];
  readonly children: ReactNode;
}

const NO_TARGETS: readonly SettingsTarget[] = [];

export function AppShell({
  area,
  groups,
  organizationId,
  navLabel,
  actions,
  settingsSearch,
  status,
  sidebarFoot,
  staffMenuItems,
  children,
}: AppShellProps) {
  const { t } = useTranslation('console');
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // The narrow-screen navigation sheet is open on the page it was opened on: a route change closes it.
  const [navOpenOn, setNavOpenOn] = useState<string | null>(null);
  const navOpen = navOpenOn === pathname;
  const setNavOpen = (open: boolean) => setNavOpenOn(open ? pathname : null);

  // `Ctrl/Cmd-K` opens the palette from anywhere (spec/13 §2); it toggles, so the same keys close it.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const go = useCallback(
    (item: NavItem) => {
      void navigate(item.params === undefined ? { to: item.to } : { to: item.to, params: item.params });
    },
    [navigate],
  );

  // The layouts build their targets on every render; keyed by what they name, so the index is not.
  const targetsKey = (settingsSearch ?? NO_TARGETS).map((target) => `${target.id}=${target.scopeLabel}=${target.to}=${JSON.stringify(target.params)}`).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `targetsKey` is the targets' content.
  const targets = useMemo(() => settingsSearch ?? NO_TARGETS, [targetsKey]);
  // Read only while the palette is open: a closed palette holds no settings queries.
  const { search: searchSettings } = useSettingsSearch(paletteOpen ? targets : NO_TARGETS, paletteOpen);
  const search = useMemo(() => {
    if (targets.length === 0) return undefined;
    const open = (doc: SettingDoc) => {
      // The key as the hash: the link can be shared, and the screen reveals the field when the hash
      // changes (settings/reveal.ts). The same key again is no change to the router, so it is asked.
      const again = settingKeyFromHash(window.location.hash) === doc.anchor;
      void navigate({ to: doc.target.to, params: { ...doc.target.params, group: doc.group }, hash: doc.anchor } as never).then(() => {
        if (again) requestRevealSetting(doc.anchor);
      });
    };
    return (query: string): PaletteSection[] => {
      const found = searchSettings(query);
      if (found.length === 0) return [];
      return [
        {
          id: 'settings',
          label: t('palette.settings.title'),
          items: found.map((doc) => ({
            id: `setting:${doc.id}`,
            label: doc.label,
            icon: <NavIcon name="settings" />,
            detail: t('palette.settings.detail', { group: doc.groupLabel, scope: doc.target.scopeLabel }),
            ...(doc.readOnly ? { hint: t('palette.settings.readOnly') } : {}),
            run: () => open(doc),
          })),
        },
      ];
    };
  }, [targets, searchSettings, navigate, t]);

  const sections = useMemo<PaletteSection[]>(() => {
    const goTo: PaletteSection = {
      id: 'go-to',
      label: t('palette.goTo'),
      items: groups.flatMap((group) =>
        group.items.map((item) => ({
          id: item.id,
          label: item.label,
          icon: item.icon,
          run: () => go(item),
        })),
      ),
    };
    const built: PaletteSection[] = [goTo];
    if (actions !== undefined && actions.length > 0) {
      built.push({ id: 'actions', label: t('palette.actions'), items: actions });
    }
    return built;
  }, [groups, actions, go, t]);

  const label = navLabel ?? t('shell.mainNav');

  return (
    <div className="console-shell">
      <a className="console-skip" href="#console-main">
        {t('app.skipToContent')}
      </a>
      <div className="console-sidebar-col">
        <aside className="console-sidebar">
          <Brand />
          <SideNav groups={groups} label={label} />
          {sidebarFoot === undefined ? null : <div className="console-sidebar__foot">{sidebarFoot}</div>}
        </aside>
      </div>
      <div className="console-frame">
        <header className="console-topbar">
          <Button
            variant="ghost"
            size="sm"
            className="console-topbar__menu"
            icon={<Icon name="menu" />}
            label={t('shell.openNavigation')}
            aria-expanded={navOpen}
            onClick={() => setNavOpen(true)}
          />
          <span className="console-topbar__brand">
            <Brand compact />
          </span>
          <div className="console-topbar__area">{area}</div>
          <div className="console-topbar__spring" />
          <button
            type="button"
            className="console-omni"
            onClick={() => setPaletteOpen(true)}
            aria-keyshortcuts="Control+K"
            // Named outright: on a phone the words are hidden and only the icon shows.
            aria-label={t('palette.open')}
          >
            <span className="console-omni__icon" aria-hidden="true">
              <SearchIcon />
            </span>
            <span className="console-omni__text">{t('palette.open')}</span>
            <Kbd keys={['Ctrl', 'K']} className="console-omni__kbd" />
          </button>
          {status}
          <NotificationsBell {...(organizationId === undefined ? {} : { organizationId })} />
          <StaffMenu items={staffMenuItems} />
        </header>
        <main id="console-main" className="console-main" tabIndex={-1}>
          {/* A page arrives with a short fade and rise; the same path keeps its page (ui-web RouteTransition). */}
          <RouteTransition routeKey={pathname}>{children}</RouteTransition>
        </main>
      </div>
      <Sheet side="left" size="sm" title={t('shell.navigation')} open={navOpen} onOpenChange={setNavOpen} className="console-navsheet">
        {navOpen ? (
          <>
            <SideNav groups={groups} label={label} />
            {sidebarFoot === undefined ? null : <div className="console-sidebar__foot">{sidebarFoot}</div>}
          </>
        ) : null}
      </Sheet>
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        sections={sections}
        {...(search === undefined ? {} : { search })}
      />
    </div>
  );
}

function Brand({ compact = false }: { readonly compact?: boolean }): ReactNode {
  return (
    <Link to="/" className={compact ? 'console-brand console-brand--compact' : 'console-brand'} aria-label="Clavian">
      <Mark size={compact ? 24 : 26} />
      {compact ? null : (
        <span className="console-wordmark" aria-hidden="true">
          Clavian<span className="console-wordmark__stop">.</span>
        </span>
      )}
    </Link>
  );
}

function SideNav({ groups, label }: { readonly groups: readonly NavGroup[]; readonly label: string }): ReactNode {
  return (
    <nav className="console-nav" aria-label={label}>
      {groups.map((group) => (
        <div className="console-navgroup" key={group.id}>
          <p className="console-navgroup__label">{group.label}</p>
          <ul>
            {group.items.map((item) => (
              <li key={item.id}>
                {item.current === undefined ? (
                  <Link
                    to={item.to}
                    params={item.params}
                    className="console-navlink"
                    activeProps={{ 'aria-current': 'page' }}
                    activeOptions={{ exact: item.exact === true }}
                    // What the console tour points at (setup/ShellTour.tsx).
                    data-tour={item.id}
                    {...(item.badge === undefined ? {} : { 'aria-label': item.badge.label })}
                  >
                    <NavLinkBody item={item} />
                  </Link>
                ) : (
                  // `exact`: the router marks a link it finds active `aria-current` itself, and a
                  // path below this one may be another item's — one organisation's settings are
                  // Platform settings', though they sit under Organisations' `/organizations`.
                  <Link
                    to={item.to}
                    params={item.params}
                    className="console-navlink"
                    activeOptions={{ exact: true }}
                    aria-current={item.current ? 'page' : undefined}
                    data-tour={item.id}
                    {...(item.badge === undefined ? {} : { 'aria-label': item.badge.label })}
                  >
                    <NavLinkBody item={item} />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function NavLinkBody({ item }: { readonly item: NavItem }): ReactNode {
  return (
    <>
      <span className="console-navlink__icon">{item.icon}</span>
      <span className="console-navlink__label">{item.label}</span>
      {item.badge === undefined ? null : (
        <span className="console-navlink__count" aria-hidden="true">
          {item.badge.text}
        </span>
      )}
    </>
  );
}

/** The organisation's own settings, at the foot of the sidebar. */
export function OrganizationLink({ scope }: { readonly scope: ConsoleScope }): ReactNode {
  const { t } = useTranslation('console');
  const organization = scope.organization;
  if (organization === undefined) return null;
  return (
    <Link
      to="/o/$orgSlug/settings"
      params={{ orgSlug: organization.slug }}
      className="console-orglink"
      activeProps={{ 'aria-current': 'page' }}
      aria-label={t('shell.organisationSettings', { organisation: organization.name })}
    >
      <span className="console-orglink__icon" aria-hidden="true">
        <NavIcon name="organizations" />
      </span>
      <span className="console-orglink__text">
        <span className="console-orglink__eyebrow" aria-hidden="true">
          {t('shell.organisation')}
        </span>
        <span className="console-orglink__name">{organization.name}</span>
      </span>
    </Link>
  );
}

/**
 * A screen that must be asked before signing out (ADR-0032: "You're still holding the Front desk drawer"): it returns
 * true when it took over — showed its own question — and calls the second argument to sign out after all.
 */
export type SignOutGuard = (signOutNow: () => Promise<void>) => boolean;
export const SignOutGuardContext = createContext<SignOutGuard | null>(null);

/** Sign the member out — the same everywhere, so the top bar and the palette share it — asking the guard first. */
export function useSignOut(): () => Promise<void> {
  const now = useSignOutNow();
  const guard = useContext(SignOutGuardContext);
  return useCallback(async () => {
    if (guard !== null && guard(now)) return;
    await now();
  }, [guard, now]);
}

/** Sign out without asking. */
export function useSignOutNow(): () => Promise<void> {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useCallback(async () => {
    try {
      await api.signOut();
    } finally {
      queryClient.clear();
      await navigate({ to: '/sign-in' });
    }
  }, [api, queryClient, navigate]);
}

/** Who is signed in, the theme, and signing out — the same in both consoles. */
export function StaffMenu({ items = [] }: { readonly items?: readonly MenuEntry[] }) {
  const { t } = useTranslation('console');
  const session = useSession().data;
  const signOut = useSignOut();
  const { resolved } = useConsoleTheme();
  const user = session?.user;
  const name = user === undefined ? '' : user.name || user.displayUsername || user.email;

  return (
    <Menu
      className="console-user-menu__menu"
      items={[
        ...(name ? [{ id: 'who', kind: 'label' as const, label: t('shell.staffMenu', { name }) }, { id: 'sep-who', kind: 'separator' as const }] : []),
        ...items,
        ...(items.length > 0 ? [{ id: 'sep-out', kind: 'separator' as const }] : []),
        // The quick switch (ADR-0041): to the other theme, by name. Your account → Appearance has "Match this device".
        resolved === 'dark'
          ? { id: 'theme', label: t('shell.useLightTheme'), icon: <Icon name="sun" />, onSelect: () => setConsoleTheme('light') }
          : { id: 'theme', label: t('shell.useDarkTheme'), icon: <Icon name="moon" />, onSelect: () => setConsoleTheme('dark') },
        { id: 'sign-out', label: t('shell.signOut'), icon: <NavIcon name="signout" />, onSelect: () => void signOut() },
      ]}
      trigger={
        <button type="button" className="console-user-menu" aria-label={name ? t('shell.staffMenuOpen', { name }) : t('shell.staffMenuOpenAnonymous')}>
          <Avatar name={name || '?'} size="sm" decorative />
          {name ? (
            <span className="console-user-menu__name" aria-hidden="true">
              {name}
            </span>
          ) : null}
          <span className="console-user-menu__chevron" aria-hidden="true">
            <Icon name="chevronDown" />
          </span>
        </button>
      }
    />
  );
}
